import React from 'react';
import { Container } from 'components/shared/Container';
import { useTranslations } from 'next-intl';
import styles from './TableFAQ.module.css';

type FaqItem = {
  question: string;
  answer: string;
};

type TableFAQProps = {
  title?: string;
  faqItems: FaqItem[];
  bgColor?: 'white' | 'black';
  /** Skip the shared Container so the host page can supply its own gutter. */
  disableContainer?: boolean;
  gutter?: React.ComponentType<{ children?: React.ReactNode }>;
};

const TableFAQ: React.FC<TableFAQProps> = ({
  title,
  faqItems,
  bgColor = 'white',
  disableContainer = false,
  gutter: Gutter,
}) => {
  const t = useTranslations('common');
  const resolvedTitle = title ?? t('tableFAQ.title');
  const body = (
    <>
      <h2
        className={styles.title}
        id="faq-heading"
        aria-label="" // WEB ACCESSIBILITY MAINTENANCE : Added aria-label | Should describe the FAQ section purpose
      >
        {resolvedTitle}
      </h2>

      {/* Desktop table layout */}
      <div className={styles.desktopOnly}>
        <table
          className={styles.table}
          role="table"
          aria-labelledby="faq-heading"
          aria-describedby="" // WEB ACCESSIBILITY MAINTENANCE : Added aria-describedby | Link to description of FAQ table if needed
        >
          <tbody>
            {faqItems.map((item, idx) => (
              <React.Fragment key={idx}>
                <tr>
                  <td
                    className={styles.qCell}
                    role="rowheader"
                    aria-label={t('tableFAQ.qCell.ariaLabel.question')}
                    tabIndex={0} // WEB ACCESSIBILITY MAINTENANCE : Added tabIndex | Makes the question focusable for keyboard navigation
                  >
                    {item.question}
                  </td>
                  <td
                    className={styles.aCell}
                    role="cell"
                    aria-label={t('tableFAQ.aCell.ariaLabel.answer')}
                    data-bg={bgColor}
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </tr>
                <tr className={styles.tableSpacer} />
                <tr>
                  <td colSpan={2}>
                    <div
                      className={styles.divider}
                      data-bg={bgColor}
                      role="separator" // WEB ACCESSIBILITY MAINTENANCE : Added role separator | Indicates visual separator between FAQ items
                      aria-hidden="true" // WEB ACCESSIBILITY MAINTENANCE : Added aria-hidden | Hides decorative divider from screen readers
                    />
                  </td>
                </tr>
                {idx !== faqItems.length - 1 && (
                  <>
                    <tr className={styles.tableSpacer} />
                  </>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked layout */}
      <div
        className={styles.mobileOnly}
        aria-labelledby="faq-heading"
        role="group"
        aria-label="" // WEB ACCESSIBILITY MAINTENANCE : Added aria-label | Should describe the mobile FAQ group
      >
        {faqItems.map((item, idx) => (
          <React.Fragment key={idx}>
            <div className={styles.mobileItem}>
              <h3
                className={styles.mq}
                role="heading"
                aria-level={3}
                tabIndex={0} // WEB ACCESSIBILITY MAINTENANCE : Added tabIndex | Makes the question focusable for keyboard navigation
              >
                {item.question}
              </h3>
              <div
                className={styles.ma}
                data-bg={bgColor}
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </div>
            <div className={styles.mobileGap} />

            <div
              className={styles.divider}
              data-bg={bgColor}
              role="separator" // WEB ACCESSIBILITY MAINTENANCE : Added role separator | Indicates visual separator between FAQ items
              aria-hidden="true" // WEB ACCESSIBILITY MAINTENANCE : Added aria-hidden | Hides decorative divider from screen readers
            />
            {idx !== faqItems.length - 1 && (
              <div className={styles.mobileGap} />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );

  return (
    <section
      className={styles.section}
      data-bg={bgColor}
      data-uncontained={disableContainer ? '' : undefined}
    >
      {disableContainer ? (
        Gutter ? (
          <Gutter>{body}</Gutter>
        ) : (
          body
        )
      ) : (
        <Container>{body}</Container>
      )}
    </section>
  );
};

export default TableFAQ;

/*
WEB ACCESSIBILITY MAINTENANCE : STRUCTURAL CHANGES NEEDED

1. SEMANTIC HTML
   - Current: Using table and divs for FAQ layout
   - Required: Consider using definition list (<dl>, <dt>, <dd>) for question-answer pairs
   - Action: Evaluate if <dl> structure would be more semantically appropriate for FAQ content

2. KEYBOARD NAVIGATION
   - Current: Basic tabIndex added to questions
   - Required: Full keyboard support for FAQ navigation
   - Action:
     * Consider implementing keyboard shortcuts for FAQ navigation
     * Add focus indicators that are clearly visible
     * Test with keyboard-only navigation

3. SCREEN READER ANNOUNCEMENTS
   - Current: Basic ARIA roles added
   - Required: Ensure proper announcement of question-answer relationships
   - Action: Test with screen readers (NVDA, JAWS, VoiceOver) to ensure proper announcements

4. FOCUS MANAGEMENT
   - Current: Basic tabIndex on questions
   - Required: Clear focus indicators and logical focus order
   - Action:
     * Add visible focus styles (outline) for keyboard users
     * Ensure focus order follows logical reading order
     * Test focus visible with keyboard navigation

5. DYNAMIC CONTENT
   - Current: Static HTML content in answers
   - Required: Ensure dynamically inserted content is accessible
   - Action:
     * Validate that HTML content in answers maintains proper structure
     * Ensure links within answers are keyboard accessible
     * Test that list structures in answers are properly announced

6. COLOR CONTRAST
   - Current: Using opacity-based colors for answers
   - Required: Ensure WCAG 2.2 AA compliance (4.5:1 for normal text)
   - Action:
     * Test color contrast ratios for all text
     * Verify contrast in both light and dark modes
     * Ensure links have sufficient contrast

7. RESPONSIVE BEHAVIOR
   - Current: Different layouts for mobile and desktop
   - Required: Ensure accessibility in both layouts
   - Action:
     * Test screen reader behavior on both mobile and desktop
     * Verify keyboard navigation works in both layouts
     * Ensure focus indicators are visible at all viewport sizes
*/
